import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MdDelete, MdArrowBack, MdError, MdWarning } from 'react-icons/md';
import { FaImage, FaVideo, FaYoutube, FaFacebook, FaInstagram, FaTwitter, FaTiktok, FaLinkedin, FaReddit, FaPinterest, FaRegCalendarCheck, FaSync } from 'react-icons/fa';
import { BsThreads } from 'react-icons/bs';
import { CiWarning, CiCalendar } from "react-icons/ci";
import { v4 as uuidv4 } from 'uuid';
import { getFileType, getFileTypeFromFirebasePath } from '../../../utils/getFileType';
import { useWorkspace } from '../../../context/WorkspaceContext';
import MultipleUploadArea from '../UploadForm/components/MultipleUploadArea';
import CheckboxBtn from '../Checkbox/Checkboxbtn';
import Textarea from '../../Tools/Textarea';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../Tools/DatePickerStyles.css';
import axios from 'axios';
import { Button } from '../../../ui/button';
import RedditSettings from '../UploadForm/components/RedditSettings';
import PinterestSettings from '../UploadForm/components/PinterestSettings';
import TikTokSettings from '../UploadForm/components/TikTokSettings';
import useThemeColors from '../../../hooks/useThemeColors';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const BulkUpload = ({ onFormAction, isUploadLimitReached, quotaLoading }) => {
    const [currentStep, setCurrentStep] = useState('upload'); // 'upload' or 'configure'
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [userSocials, setUserSocials] = useState([]);
    const [globalPlatforms, setGlobalPlatforms] = useState([]); // Global platform selection
    const [isUploading, setIsUploading] = useState(false);
    const [ongoingUploads, setOngoingUploads] = useState([]); // Track ongoing uploads
    const [datePickerOpenFiles, setDatePickerOpenFiles] = useState(new Set());
    const [errorMessages, setErrorMessages] = useState([]);
    const [warningMessages, setWarningMessages] = useState([]);
    const { activeWorkspace, idToken } = useWorkspace();
    const { colors, isDark } = useThemeColors();

    // Platform icons mapping
    const platformIcons = {
        youtube: <FaYoutube color="red" />,
        facebook: <FaFacebook color="blue" />,
        instagram: <FaInstagram color="pink" />,
        x: <FaTwitter color="black" />,
        tiktok: <FaTiktok color="black" />,
        linkedin: <FaLinkedin color="blue" />,
        reddit: <FaReddit color="red" />,
        pinterest: <FaPinterest color="red" />,
        threads: <BsThreads color="black" />
    };

    // Get user social media accounts
    const getUserSocials = useCallback(async () => {
        if (!idToken || !activeWorkspace.id) return;

        try {
            const response = await axios.get(BACKEND_API_URL + '/api/auth/user/social_media_tokens', {
                params: { workspaceId: activeWorkspace.id },
                headers: { Authorization: `Bearer ${idToken}` },
            });

            response.data.forEach((item) => {
                item.checkbox = false;
            });

            setUserSocials(response.data);
        } catch (error) {
            console.error('Error fetching social media tokens:', error);
        }
    }, [idToken, activeWorkspace.id]);

    // Listen for localStorage changes from other tabs (social auth completion)
    useEffect(() => {
        // Function to check if social auth was successful
        const checkAuthSuccess = () => {
            const authSuccess = localStorage.getItem('social_auth_success');
            if (authSuccess) {
                // Clear the flag
                localStorage.removeItem('social_auth_success');
                // Refresh the socials list
                getUserSocials();
            }
        };

        // Function to handle focus events for pending auth
        const handleFocus = () => {
            const pendingAuth = localStorage.getItem('social_auth_pending');
            if (pendingAuth) {
                // When the user returns to this tab, check if there are pending auths and refresh
                getUserSocials();
                localStorage.removeItem('social_auth_pending');
            }
        };

        // Check immediately (for when returning to an already-open tab)
        checkAuthSuccess();

        // Set up event listener for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'social_auth_success') {
                checkAuthSuccess();
            }
        };

        // Listen for when the window regains focus
        window.addEventListener('focus', handleFocus);
        window.addEventListener('storage', handleStorageChange);
        
        // Clean up the event listeners
        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [getUserSocials]);

    useEffect(() => {
        if (activeWorkspace.id && idToken) {
            getUserSocials();
        }
    }, [getUserSocials, activeWorkspace.id, idToken]);

    // Toggle global platform selection
    const toggleGlobalPlatform = (platformId, platform) => {
        setGlobalPlatforms(prevPlatforms => {
            const isSelected = prevPlatforms.some(p => p.platformId === platformId);
            
            if (isSelected) {
                return prevPlatforms.filter(p => p.platformId !== platformId);
            } else {
                return [...prevPlatforms, { platformId, platform }];
            }
        });
    };

    // Comprehensive validation logic
    useEffect(() => {
        const errors = [];
        const warnings = [];

        // Check if no files are uploaded
        if (uploadedFiles.length === 0) {
            setErrorMessages([]);
            setWarningMessages([]);
            return;
        }

        // Check if any global platforms are selected
        if (globalPlatforms.length === 0) {
            warnings.push({
                message: `No platforms selected. All files will be saved as drafts only.`,
                icon: CiWarning,
                color: 'yellow'
            });
        }

        // Create maps to group similar errors/warnings by file and error type
        const errorMap = new Map();
        const warningMap = new Map();

        // Validate each file with global platforms
        uploadedFiles.forEach((file, index) => {
            const fileDisplayName = `File ${index + 1} (${file.name})`;
            const fileType = getFileType(file.uploadData.uploadType);

            // Maps to track platforms with specific issues for this file
            const titleMissingPlatforms = [];
            const descriptionMissingPlatforms = [];
            const subredditMissingPlatforms = [];
            const boardMissingPlatforms = [];

            // Validate each selected global platform for this file
            globalPlatforms.forEach(platform => {
                const platformSocial = userSocials.find(s => s.platform_id === platform.platformId);
                if (!platformSocial) return;

                // Title validation for platforms that require it
                if (['youtube', 'facebook', 'reddit'].includes(platformSocial.platform)) {
                    if (!file.uploadData.title || file.uploadData.title.trim() === '') {
                        titleMissingPlatforms.push(platformSocial.platform);
                    }
                }

                // Description validation
                if (!file.uploadData.description || file.uploadData.description.trim() === '') {
                    descriptionMissingPlatforms.push(platformSocial.platform);
                }

                // Reddit subreddit validation
                if (platformSocial.platform === 'reddit' && file.uploadData.subreddits.length === 0) {
                    subredditMissingPlatforms.push(platformSocial.platform);
                }

                // Pinterest board validation
                if (platformSocial.platform === 'pinterest' && file.uploadData.board_ids.length === 0) {
                    boardMissingPlatforms.push(platformSocial.platform);
                }
            });

            // Collect errors for merging
            if (titleMissingPlatforms.length > 0) {
                const platformNames = titleMissingPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
                const verb = titleMissingPlatforms.length === 1 ? 'requires' : 'require';
                const errorKey = `${platformNames} ${verb} a title.`;
                
                if (!errorMap.has(errorKey)) {
                    errorMap.set(errorKey, {
                        files: [],
                        icon: MdError,
                        color: 'red'
                    });
                }
                errorMap.get(errorKey).files.push(index + 1);
            }

            if (descriptionMissingPlatforms.length > 0) {
                const platformNames = descriptionMissingPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
                const verb = descriptionMissingPlatforms.length === 1 ? 'is' : 'are';
                const warningKey = `${platformNames} ${verb} missing a description.`;
                
                if (!warningMap.has(warningKey)) {
                    warningMap.set(warningKey, {
                        files: [],
                        icon: CiWarning,
                        color: 'yellow'
                    });
                }
                warningMap.get(warningKey).files.push(index + 1);
            }

            if (subredditMissingPlatforms.length > 0) {
                const verb = subredditMissingPlatforms.length === 1 ? 'requires' : 'require';
                const errorKey = `Reddit ${verb} a subreddit selection.`;
                
                if (!errorMap.has(errorKey)) {
                    errorMap.set(errorKey, {
                        files: [],
                        icon: MdError,
                        color: 'red'
                    });
                }
                errorMap.get(errorKey).files.push(index + 1);
            }

            if (boardMissingPlatforms.length > 0) {
                const verb = boardMissingPlatforms.length === 1 ? 'requires' : 'require';
                const errorKey = `Pinterest ${verb} a board selection.`;
                
                if (!errorMap.has(errorKey)) {
                    errorMap.set(errorKey, {
                        files: [],
                        icon: MdError,
                        color: 'red'
                    });
                }
                errorMap.get(errorKey).files.push(index + 1);
            }

        });

        // Convert error and warning maps to final arrays with merged file numbers
        errorMap.forEach((value, key) => {
            const fileNumbers = value.files.length > 1 ? `File ${value.files.join(', ')}: ` : `File ${value.files[0]}: `;
            errors.push({
                message: `${fileNumbers}${key}`,
                icon: value.icon,
                color: value.color
            });
        });

        warningMap.forEach((value, key) => {
            const fileNumbers = value.files.length > 1 ? `File ${value.files.join(', ')}: ` : `File ${value.files[0]}: `;
            warnings.push({
                message: `${fileNumbers}${key}`,
                icon: value.icon,
                color: value.color
            });
        });

        // Media file validation - process each platform again for media-specific rules
        const mediaErrorMap = new Map();
        const mediaWarningMap = new Map();

        uploadedFiles.forEach((file, index) => {
            const fileDisplayName = `File ${index + 1} (${file.name})`;
            const fileType = getFileType(file.uploadData.uploadType);

            globalPlatforms.forEach(platform => {
                const platformSocial = userSocials.find(s => s.platform_id === platform.platformId);
                if (!platformSocial) return;

                // Media file validation
                if (file.uploadData.firebasePath) {
                    // TikTok specific validation
                    if (platformSocial.platform === 'tiktok') {
                        if (fileType === 'video') {
                            // Video format validation
                            const urlWithoutParams = file.uploadData.firebasePath.split('?')[0];
                            const fileExtension = urlWithoutParams.split('.').pop().toLowerCase();
                            const supportedVideoFormats = ['mp4', 'webm', 'mov'];
                            if (!supportedVideoFormats.includes(fileExtension)) {
                                const errorKey = `TikTok only supports MP4, WebM, and MOV video formats. Your video is ${fileExtension}.`;
                                if (!mediaErrorMap.has(errorKey)) {
                                    mediaErrorMap.set(errorKey, {
                                        files: [],
                                        icon: MdError,
                                        color: 'red'
                                    });
                                }
                                mediaErrorMap.get(errorKey).files.push(index + 1);
                            }
                        } else if (fileType === 'image') {
                            // Image format validation for TikTok
                            const urlWithoutParams = file.uploadData.firebasePath.split('?')[0];
                            const fileExtension = urlWithoutParams.split('.').pop().toLowerCase();
                            const supportedImageFormats = ['webp', 'jpeg', 'jpg'];
                            if (!supportedImageFormats.includes(fileExtension)) {
                                const errorKey = `TikTok only supports WebP and JPEG/JPG image formats. Your image is ${fileExtension}.`;
                                if (!mediaErrorMap.has(errorKey)) {
                                    mediaErrorMap.set(errorKey, {
                                        files: [],
                                        icon: MdError,
                                        color: 'red'
                                    });
                                }
                                mediaErrorMap.get(errorKey).files.push(index + 1);
                            }

                            // Check image dimensions for TikTok
                            const img = new Image();
                            img.onload = () => {
                                if (img.width > 1080 || img.height > 1080) {
                                    const errorKey = `TikTok images cannot exceed 1080x1080 pixels.`;
                                    const fileInfo = { fileIndex: index + 1, dimensions: `${img.width}x${img.height}` };
                                    
                                    setErrorMessages(prev => {
                                        // Check if this error type already exists
                                        const existingErrorIndex = prev.findIndex(error => 
                                            error.message.includes(errorKey) && error.color === 'red'
                                        );
                                        
                                        if (existingErrorIndex !== -1) {
                                            // Update existing error to include this file
                                            const updated = [...prev];
                                            const existingError = updated[existingErrorIndex];
                                            const fileNumbers = existingError.message.match(/File ([\d, ]+):/);
                                            const currentFiles = fileNumbers ? fileNumbers[1].split(', ').map(n => parseInt(n)) : [];
                                            
                                            if (!currentFiles.includes(index + 1)) {
                                                currentFiles.push(index + 1);
                                                currentFiles.sort((a, b) => a - b);
                                                updated[existingErrorIndex] = {
                                                    ...existingError,
                                                    message: `File ${currentFiles.join(', ')}: ${errorKey}`
                                                };
                                            }
                                            return updated;
                                        } else {
                                            // Add new error
                                            return [...prev, {
                                                message: `File ${index + 1}: ${errorKey}`,
                                                icon: MdError,
                                                color: 'red'
                                            }];
                                        }
                                    });
                                }
                            };
                            img.src = file.uploadData.firebasePath;
                        }
                    }

                    // Instagram specific validation
                    if (platformSocial.platform === 'instagram' && fileType === 'image') {
                        const img = new Image();
                        img.onload = () => {
                            const aspectRatio = img.width / img.height;
                            if (aspectRatio < 0.8 || aspectRatio > 1.91) {
                                const errorKey = `Instagram image aspect ratio must be between 4:5 (0.8) and 1.91:1 (1.91).`;
                                
                                setErrorMessages(prev => {
                                    // Check if this error type already exists
                                    const existingErrorIndex = prev.findIndex(error => 
                                        error.message.includes(errorKey) && error.color === 'red'
                                    );
                                    
                                    if (existingErrorIndex !== -1) {
                                        // Update existing error to include this file
                                        const updated = [...prev];
                                        const existingError = updated[existingErrorIndex];
                                        const fileNumbers = existingError.message.match(/File ([\d, ]+):/);
                                        const currentFiles = fileNumbers ? fileNumbers[1].split(', ').map(n => parseInt(n)) : [];
                                        
                                        if (!currentFiles.includes(index + 1)) {
                                            currentFiles.push(index + 1);
                                            currentFiles.sort((a, b) => a - b);
                                            updated[existingErrorIndex] = {
                                                ...existingError,
                                                message: `File ${currentFiles.join(', ')}: ${errorKey}`
                                            };
                                        }
                                        return updated;
                                    } else {
                                        // Add new error
                                        return [...prev, {
                                            message: `File ${index + 1}: ${errorKey}`,
                                            icon: MdError,
                                            color: 'red'
                                        }];
                                    }
                                });
                            }

                            if (img.width < 320 || img.width > 1440) {
                                const errorKey = `Instagram image width must be between 320px and 1440px.`;
                                
                                setErrorMessages(prev => {
                                    // Check if this error type already exists
                                    const existingErrorIndex = prev.findIndex(error => 
                                        error.message.includes(errorKey) && error.color === 'red'
                                    );
                                    
                                    if (existingErrorIndex !== -1) {
                                        // Update existing error to include this file
                                        const updated = [...prev];
                                        const existingError = updated[existingErrorIndex];
                                        const fileNumbers = existingError.message.match(/File ([\d, ]+):/);
                                        const currentFiles = fileNumbers ? fileNumbers[1].split(', ').map(n => parseInt(n)) : [];
                                        
                                        if (!currentFiles.includes(index + 1)) {
                                            currentFiles.push(index + 1);
                                            currentFiles.sort((a, b) => a - b);
                                            updated[existingErrorIndex] = {
                                                ...existingError,
                                                message: `File ${currentFiles.join(', ')}: ${errorKey}`
                                            };
                                        }
                                        return updated;
                                    } else {
                                        // Add new error
                                        return [...prev, {
                                            message: `File ${index + 1}: ${errorKey}`,
                                            icon: MdError,
                                            color: 'red'
                                        }];
                                    }
                                });
                            }
                        };
                        img.src = file.uploadData.firebasePath;
                    }

                    // Reddit specific validation
                    if (platformSocial.platform === 'reddit') {
                        // Check image size for Reddit (500 KiB limit)
                        if (fileType === 'image') {
                            fetch(file.uploadData.firebasePath)
                                .then(response => response.blob())
                                .then(blob => {
                                    const fileSizeKiB = blob.size / 1024;
                                    const redditSizeLimit = 500;
                                    if (fileSizeKiB > redditSizeLimit) {
                                        const warningKey = `Reddit image size exceeds limit and will be automatically compressed.`;
                                        
                                        setWarningMessages(prev => {
                                            // Check if this warning type already exists
                                            const existingWarningIndex = prev.findIndex(warning => 
                                                warning.message.includes(warningKey) && warning.color === 'yellow'
                                            );
                                            
                                            if (existingWarningIndex !== -1) {
                                                // Update existing warning to include this file
                                                const updated = [...prev];
                                                const existingWarning = updated[existingWarningIndex];
                                                const fileNumbers = existingWarning.message.match(/File ([\d, ]+):/);
                                                const currentFiles = fileNumbers ? fileNumbers[1].split(', ').map(n => parseInt(n)) : [];
                                                
                                                if (!currentFiles.includes(index + 1)) {
                                                    currentFiles.push(index + 1);
                                                    currentFiles.sort((a, b) => a - b);
                                                    updated[existingWarningIndex] = {
                                                        ...existingWarning,
                                                        message: `File ${currentFiles.join(', ')}: ${warningKey}`
                                                    };
                                                }
                                                return updated;
                                            } else {
                                                // Add new warning
                                                return [...prev, {
                                                    message: `File ${index + 1}: ${warningKey}`,
                                                    icon: MdWarning,
                                                    color: 'yellow'
                                                }];
                                            }
                                        });
                                    }
                                })
                                .catch(error => {
                                    console.error('Error checking Reddit image size:', error);
                                });
                        }
                    }
                }
            });
        });

        // Convert media error maps to final arrays
        mediaErrorMap.forEach((value, key) => {
            const fileNumbers = value.files.length > 1 ? `File ${value.files.join(', ')}: ` : `File ${value.files[0]}: `;
            errors.push({
                message: `${fileNumbers}${key}`,
                icon: value.icon,
                color: value.color
            });
        });

        mediaWarningMap.forEach((value, key) => {
            const fileNumbers = value.files.length > 1 ? `File ${value.files.join(', ')}: ` : `File ${value.files[0]}: `;
            warnings.push({
                message: `${fileNumbers}${key}`,
                icon: value.icon,
                color: value.color
            });
        });

        setErrorMessages(errors);
        setWarningMessages(warnings);
    }, [uploadedFiles, userSocials, globalPlatforms]);

    // Handle file selection from UploadArea
    const handleFileSelect = (firebasePath, thumbnailPath) => {
        if (quotaLoading) {
            return;
        }

        if (isUploadLimitReached) {
            toast.error('Upload limit reached. Please upgrade your plan to continue uploading.');
            return;
        }
        
        const fileType = getFileTypeFromFirebasePath(firebasePath);
        const fileName = firebasePath.split('/').pop().split('.')[0];

        const newFile = {
            id: uuidv4(),
            name: fileName,
            type: fileType,
            uploadData: {
                title: '',
                description: '',
                privacyStatus: 'public',
                uploadTo: 'draft',
                uploadType: fileType,
                platforms: [],
                draftId: null,
                postId: uuidv4(),
                platformId: null,
                comment: '',
                hasBeenEdited: false,
                subreddits: [],
                board_ids: [],
                link: '',
                alt_text: '',
                schedule: null,
                firebasePath: firebasePath,
                thumbnailPath: thumbnailPath,
                videoPath: fileName,
                // TikTok specific fields
                commentEnabled: true,
                isContentDisclosed: false,
                duetEnabled: true,
                stitchEnabled: true,
                isBranded: false,
                isOwnBrand: false,
            }
        };

        setUploadedFiles(prevFiles => {
            const newFiles = [...prevFiles, newFile];
            // Auto-advance to configure step after first file upload
            if (prevFiles.length === 0 && currentStep === 'upload') {
                setCurrentStep('configure');
            }
            return newFiles;
        });
    };

    // Remove uploaded file
    const removeFile = (fileId) => {
        setUploadedFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    };

    // Update file data
    const updateFileData = (fileId, updatedData) => {
        setUploadedFiles(prevFiles =>
            prevFiles.map(file =>
                file.id === fileId
                    ? { ...file, uploadData: { ...file.uploadData, ...updatedData, hasBeenEdited: true } }
                    : file
            )
        );
    };

    // Handle date picker toggle
    const toggleDatePicker = (fileId) => {
        setDatePickerOpenFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            } else {
                newSet.add(fileId);
            }
            return newSet;
        });
    };

    const handleDateChange = (fileId, date) => {
        updateFileData(fileId, { schedule: date });
        setDatePickerOpenFiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
        });
    };

    // Handle bulk action
    const handleBulkAction = async (action) => {
        if (uploadedFiles.length === 0) {
            toast.warning('No files selected for bulk upload');
            return;
        }

        if (quotaLoading) {
            return;
        }

        if (isUploadLimitReached && action !== 'draft') {
            toast.error('Upload limit reached. Please upgrade your plan to continue uploading.');
            return;
        }

        // Check for errors before proceeding with upload/schedule
        if (errorMessages.length > 0 && action !== 'draft') {
            toast.error('Please fix all errors before uploading. You can save as draft to continue later.');
            return;
        }

        setIsUploading(true);

        try {
            const allFormData = [];

            // Process each file and create form data for each globally selected platform
            uploadedFiles.forEach(file => {
                const { schedule } = file.uploadData;

                if (globalPlatforms && globalPlatforms.length > 0) {
                    globalPlatforms.forEach(platform => {
                        const formData = {
                            ...file.uploadData,
                            uploadTo: platform.platform,
                            platformId: platform.platformId,
                            firebasePath: file.uploadData.firebasePath,
                            thumbnail: file.uploadData.thumbnailPath,
                            postId: `${file.uploadData.postId}_${platform.platformId}`,
                            workspaceId: activeWorkspace.id
                        };

                        // For 'upload' action, check if file has schedule date
                        if (action === 'upload' && schedule && new Date(schedule) > new Date()) {
                            // File has future schedule date, so schedule it instead
                            allFormData.push({ ...formData, action: 'schedule' });
                        } else {
                            // Upload immediately or save as draft
                            allFormData.push({ ...formData, action: action });
                        }
                    });
                } else {
                    // If no global platforms selected, save as draft
                    const formData = {
                        ...file.uploadData,
                        uploadTo: 'draft',
                        firebasePath: file.uploadData.firebasePath,
                        thumbnail: file.uploadData.thumbnailPath,
                        workspaceId: activeWorkspace.id
                    };

                    allFormData.push({ ...formData, action: 'draft' });
                }
            });

            // Group by action type and process
            const actionGroups = allFormData.reduce((groups, item) => {
                const action = item.action;
                if (!groups[action]) groups[action] = [];
                // Remove the action property before adding to group
                const { action: _, ...itemWithoutAction } = item;
                groups[action].push(itemWithoutAction);
                return groups;
            }, {});

            // Process each action group
            for (const [actionType, items] of Object.entries(actionGroups)) {
                await onFormAction(actionType, items);
            }

        } catch (error) {
            console.error('Bulk upload error:', error);
            toast.error('An error occurred during bulk upload');
        } finally {
            setIsUploading(false);
        }
    };

    // Get file icon
    const getFileIcon = (type) => {
        switch (type) {
            case 'image': return <FaImage className="text-blue-500" size={20} />;
            case 'video': return <FaVideo className="text-red-500" size={20} />;
            default: return <FaImage className="text-gray-500" size={20} />;
        }
    };

    // Clear all files and start over
    const clearAllFiles = () => {
        setUploadedFiles([]);
        setOngoingUploads([]);
        setGlobalPlatforms([]);
        setDatePickerOpenFiles(new Set());
        setCurrentStep('upload');
        toast.info('Starting over - all files cleared');
    };

    // Handle ongoing upload tracking
    const handleUploadStart = (fileName) => {
        const uploadId = uuidv4();
        setOngoingUploads(prev => [...prev, {
            id: uploadId,
            fileName: fileName,
            status: 'uploading'
        }]);
        return uploadId;
    };

    const handleUploadComplete = (uploadId) => {
        setOngoingUploads(prev => prev.filter(upload => upload.id !== uploadId));
    };

    // Persistent Upload Status Component
    const PersistentUploadStatus = () => {
        if (ongoingUploads.length === 0) return null;

        return (
            <div className="fixed top-30 right-4 z-50 --background border rounded-lg shadow-lg p-3 min-w-[300px] max-w-[400px] overflow-y-auto">
                <h4 className="font-semibold text-sm mb-2 text-gray-700">Uploading Files ({ongoingUploads.length})</h4>
                <div className="space-y-2 max-h-32">
                    {ongoingUploads.map((upload) => (
                        <div key={upload.id} className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                            <span className="truncate text-gray-600">{upload.fileName}</span>
                            <span className="text-blue-600 text-xs">Uploading...</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // GlobalAlert component for displaying validation messages
    const GlobalAlert = React.memo(({ messages }) => {
        const [isCollapsed, setIsCollapsed] = useState(false);

        const errorCount = messages.filter(msg => msg.color === 'red').length;
        const warningCount = messages.filter(msg => msg.color === 'yellow').length;

        const getBackgroundColor = (color) => {
            switch (color) {
                case 'red':
                    return '#FEE2E2';
                case 'green':
                    return '#D1FAE5';
                case 'blue':
                    return '#DBEAFE';
                case 'yellow':
                    return '#FEF3C7';
                default:
                    return '#FFFFFF';
            }
        };

        const toggleCollapse = () => {
            setIsCollapsed(!isCollapsed);
        };

        return (
            <div className="mb-4 h-full">
                <div className="flex items-center mb-2">
                    {errorCount > 0 && (
                        <div className="flex items-center !bg-red-100 !text-red-500 px-3 py-1 rounded-l-md mr-2">
                            <MdError className="mr-1" />
                            <span className="!text-black">{errorCount}</span>
                        </div>
                    )}
                    {warningCount > 0 && (
                        <div className="flex items-center !bg-yellow-100 !text-yellow-500 px-3 py-1 rounded-r-md">
                            <MdWarning className="mr-1" />
                            <span className="!text-black">{warningCount}</span>
                        </div>
                    )}
                    {(errorCount > 0 || warningCount > 0) && (
                        <div onClick={toggleCollapse} className="text-blue-500 border-b border-blue-500 px-2 py-1 cursor-pointer ml-2 font-semibold" style={{ userSelect: 'none' }}>
                            {isCollapsed ? 'Show' : 'Hide'}
                        </div>
                    )}
                </div>
                {!isCollapsed && (errorCount > 0 || warningCount > 0) && (
                    <div>
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`flex items-center gap-3 !text-black border border-${msg.color}-200 text-${msg.color}-600 p-4 text-[14px] rounded mb-2`}
                                style={{ backgroundColor: getBackgroundColor(msg.color) }}
                            >
                                <msg.icon size={30} />
                                <span className='!text-gray-500 font-semibold'>{msg.message}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    });

    if (currentStep === 'upload') {
        return (
            <div className="h-full flex flex-col">
                {/* Persistent Upload Status */}
                <PersistentUploadStatus />

                {/* Header */}
                <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold mb-2">Bulk Upload</h2>
                    <p className="text-gray-600 text-sm">
                        Upload multiple files to get started
                    </p>
                </div>

                {/* Upload Area */}
                <div className="flex-1 flex items-start justify-center p-4">
                    <div className="w-full max-w-4xl h-[500px]">
                        <MultipleUploadArea
                            onFileSelect={handleFileSelect}
                            uploadType="bulk" // This allows both video and image uploads
                            onUploadStart={handleUploadStart}
                            onUploadComplete={handleUploadComplete}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Configure step
    return (
        <div className="h-full flex flex-col overflow-y-auto">
            {/* Persistent Upload Status */}
            <PersistentUploadStatus />

            {/* Header */}
            <div className="p-4 border-b flex-shrink-0">
                <div className="flex items-center gap-2 mb-2">
                    <button
                        onClick={clearAllFiles}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title="Start over"
                    >
                        <MdArrowBack size={20} />
                    </button>
                    <p className="text-xl font-semibold text-md">Configure Your Files</p>
                </div>
                <p className="text-gray-600 text-sm">
                    Set up each file with social media platforms and content details
                </p>
                
                {/* Global Platform Selection */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium mb-2 text-sm text-blue-900 !text-black">Select Platforms for All Files:</h4>
                    <div className="flex flex-wrap gap-2">
                        {userSocials.map((social) => (
                            <div
                                key={social.platform_id}
                                className={`cursor-pointer transition-opacity ${globalPlatforms.some(p => p.platformId === social.platform_id)
                                    ? 'opacity-100'
                                    : 'opacity-40 hover:opacity-100'
                                    }`}
                                onClick={() => toggleGlobalPlatform(social.platform_id, social.platform)}
                            >
                                <CheckboxBtn
                                    name={social.username}
                                    platform={social.platform}
                                    logo={social.logo}
                                    active={globalPlatforms.some(p => p.platformId === social.platform_id)}
                                />
                            </div>
                        ))}
                    </div>
                    {globalPlatforms.length > 0 && (
                        <p className="mt-2 text-xs text-blue-700">
                            All {uploadedFiles.length} files will be uploaded to {globalPlatforms.length} platform(s)
                        </p>
                    )}
                </div>
                
                {/* Global Alert for validation messages */}
                {(errorMessages.length > 0 || warningMessages.length > 0) && (
                    <div className="mt-4">
                        <GlobalAlert messages={[...errorMessages, ...warningMessages]} />
                    </div>
                )}
            </div>

            {/* Files Configuration */}
            <div className="flex-1 pb-32">
                <div className="p-3 space-y-3 w-[95%] mx-auto">
                    {uploadedFiles.map((file, index) => (
                        <div
                            key={file.id}
                            className="border rounded-lg p-3 --theme-colors"
                            style={{ minHeight: '280px' }}
                        >
                            {/* File Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 max-w-[calc(100%-40px)] min-w-0">
                                    {getFileIcon(file.type)}
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-medium text-base truncate" title={file.name}>{file.name}</h3>
                                        <p className="text-xs text-gray-500">File {index + 1} of {uploadedFiles.length}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeFile(file.id)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                >
                                    <MdDelete size={16} />
                                </button>
                            </div>

                            {/* Content Layout: Media (30%) + Form (70%) */}
                            <div className="flex gap-4">
                                {/* Media Preview - 30% width */}
                                <div className="w-[30%] flex-shrink-0">
                                    <div className="w-full h-full flex items-center justify-center">
                                                                {file.type === 'image' ? (
                            <img
                                src={file.uploadData.firebasePath}
                                alt={file.name}
                                className="max-w-full max-h-64 object-contain rounded border"
                                                style={{
                                                    minHeight: '100px',
                                                    maxHeight: '300px'
                                                }}
                                                onLoad={(e) => {
                                                    const img = e.target;
                                                    const aspectRatio = img.naturalWidth / img.naturalHeight;

                                                    // For very small images, don't force scaling
                                                    if (img.naturalWidth < 200 || img.naturalHeight < 200) {
                                                        img.style.maxWidth = `${img.naturalWidth}px`;
                                                        img.style.maxHeight = `${img.naturalHeight}px`;
                                                    }
                                                    // For very wide images, limit width
                                                    else if (aspectRatio > 2) {
                                                        img.style.maxWidth = '100%';
                                                        img.style.height = 'auto';
                                                    }
                                                    // For very tall images (like stories), limit height
                                                    else if (aspectRatio < 0.6) {
                                                        img.style.maxHeight = '300px';
                                                        img.style.width = 'auto';
                                                    }
                                                }}
                                            />
                                                                ) : (
                            <video
                                src={file.uploadData.firebasePath}
                                className="max-w-full max-h-64 object-contain rounded border"
                                                style={{
                                                    minHeight: '100px',
                                                    maxHeight: '300px'
                                                }}
                                                controls
                                                onLoadedMetadata={(e) => {
                                                    const video = e.target;
                                                    const aspectRatio = video.videoWidth / video.videoHeight;

                                                    // For vertical videos (shorts/reels)
                                                    if (aspectRatio < 0.8) {
                                                        video.style.maxHeight = '300px';
                                                        video.style.width = 'auto';
                                                        video.style.maxWidth = '100%';
                                                    }
                                                    // For very wide videos
                                                    else if (aspectRatio > 2) {
                                                        video.style.maxWidth = '100%';
                                                        video.style.height = 'auto';
                                                    }
                                                    // For standard videos
                                                    else {
                                                        video.style.maxWidth = '100%';
                                                        video.style.height = 'auto';
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Form Fields - 70% width */}
                                <div className="w-[70%] space-y-3">
                                    {/* Title and Privacy in one row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* Title Input */}
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Title <span className="text-[10px] italic"> (only used for youtube, facebook, reddit):</span></label>
                                            <input
                                                type="text"
                                                value={file.uploadData.title}
                                                onChange={(e) => updateFileData(file.id, { title: e.target.value })}
                                                className="w-full p-2 border rounded-md text-sm"
                                                placeholder="Enter title..."
                                            />
                                        </div>

                                        {/* Privacy Status */}
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Privacy:</label>
                                            <select
                                                value={file.uploadData.privacyStatus}
                                                onChange={(e) => updateFileData(file.id, { privacyStatus: e.target.value })}
                                                className="w-full p-2 border rounded-md text-sm"
                                            >
                                                <option value="public">Public</option>
                                                <option value="private">Private</option>
                                                <option value="unlisted">Unlisted</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Description:</label>
                                        <textarea
                                            value={file.uploadData.description}
                                            onChange={(e) => updateFileData(file.id, { description: e.target.value })}
                                            className="w-full p-2 border rounded-md text-sm h-16 resize-none"
                                            placeholder="Enter description..."
                                        />
                                    </div>

                                    {/* Comment */}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Comment:</label>
                                        <textarea
                                            value={file.uploadData.comment}
                                            onChange={(e) => updateFileData(file.id, { comment: e.target.value })}
                                            className="w-full p-2 border rounded-md text-sm h-12 resize-none"
                                            placeholder="Add a comment..."
                                        />
                                    </div>

                                    {/* Platform-specific Settings */}
                                    {globalPlatforms && globalPlatforms.length > 0 && (
                                        <div className="space-y-3">
                                            {globalPlatforms.map((platform) => {
                                                const platformSocial = userSocials.find(s => s.platform_id === platform.platformId);
                                                if (!platformSocial) return null;

                                                // Only show settings for platforms that have specific settings components
                                                const hasSettings = ['reddit', 'pinterest', 'tiktok'].includes(platformSocial.platform);
                                                if (!hasSettings) return null;

                                                return (
                                                    <div key={platform.platformId} className="border rounded-md p-3 bg-gray-50">
                                                        <h5 className="font-medium text-sm mb-2 text-gray-700 capitalize">
                                                            {platformSocial.platform} Settings
                                                        </h5>
                                                        
                                                        {platformSocial.platform === 'reddit' && (
                                                            <RedditSettings
                                                                formData={file.uploadData}
                                                                handleInputChange={(field, value) => updateFileData(file.id, { [field]: value })}
                                                                userSocials={userSocials}
                                                            />
                                                        )}
                                                        
                                                        {platformSocial.platform === 'pinterest' && (
                                                            <PinterestSettings
                                                                formData={file.uploadData}
                                                                handleInputChange={(field, value) => updateFileData(file.id, { [field]: value })}
                                                                userSocials={userSocials}
                                                            />
                                                        )}
                                                        
                                                        {platformSocial.platform === 'tiktok' && (
                                                            <TikTokSettings
                                                                formData={file.uploadData}
                                                                handleInputChange={(field, value) => updateFileData(file.id, { [field]: value })}
                                                                toggleDisclosure={() => updateFileData(file.id, { isContentDisclosed: !file.uploadData.isContentDisclosed })}
                                                                toggleBranded={() => updateFileData(file.id, { isBranded: !file.uploadData.isBranded })}
                                                                userSocials={userSocials}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Schedule Section */}
                                    <div className="mb-3 relative">
                                        <div className="flex items-center justify-between">

                                            {file.uploadData.schedule ? (
                                                <div className="mt-1 text-xs">
                                                    <span className="text-gray-600">Scheduled for: {new Date(file.uploadData.schedule).toLocaleString()}</span>
                                                    {new Date(file.uploadData.schedule) > new Date() ? (
                                                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Will be scheduled</span>
                                                    ) : (
                                                        <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Past date - will upload now</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="mt-1 text-xs">
                                                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Will upload now</span>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-2">
                                                <>
                                                    {file.uploadData.schedule ? (
                                                        <Button variant="primary" size="sm" onClick={() => toggleDatePicker(file.id)}>
                                                            <span
                                                                className="text-gray-600 cursor-pointer text-xs text-white"
                                                            >
                                                                {new Date(file.uploadData.schedule).getDate()}/{new Date(file.uploadData.schedule).getMonth() + 1} {new Date(file.uploadData.schedule).getHours().toString().padStart(2, '0')}:{new Date(file.uploadData.schedule).getMinutes().toString().padStart(2, '0')}
                                                            </span>
                                                            <span
                                                                onClick={() => updateFileData(file.id, { schedule: null })}
                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                                title="Clear schedule"
                                                            >
                                                                ×
                                                            </span>
                                                        </Button>
                                                    ) : (
                                                        <Button variant="primary" size="sm" onClick={() => toggleDatePicker(file.id)}>
                                                        <CiCalendar
                                                            color="white"
                                                            size={20}
                                                        />
                                                        <span className="text-white text-xs">Schedule</span>
                                                        </Button>
                                                    )}
                                                </>
                                                
                                                {datePickerOpenFiles.has(file.id) && (
                                                    <div className="absolute z-[60] top-10 right-0 rounded-lg">
                                                        <DatePicker
                                                            selected={file.uploadData.schedule}
                                                            onChange={(date) => handleDateChange(file.id, date)}
                                                            showTimeSelect
                                                            timeFormat="HH:mm"
                                                            timeIntervals={15}
                                                            dateFormat="MMMM d, yyyy h:mm aa"
                                                            minDate={new Date()}
                                                            onClickOutside={() => toggleDatePicker(file.id)}
                                                            calendarClassName={`custom-datepicker ${isDark ? 'dark-theme' : 'light-theme'}`}
                                                            inline
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Add More Files Section */}
                    <div className="p-3 w-full md:w-[50%] mx-auto">
                        <div className="mb-2">
                            <h3 className="text-sm font-medium mb-1 text-gray-700">Want to add more files?</h3>
                            <div className="h-[100px]">
                                <MultipleUploadArea
                                    onFileSelect={handleFileSelect}
                                    uploadType="bulk"
                                    onUploadStart={handleUploadStart}
                                    onUploadComplete={handleUploadComplete}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="fixed md:bottom-0 bottom-[50px] left-0 right-0 border-t flex py-3 items-end px-5 overflow-hidden z-10 --background">
                <div className="flex gap-2 items-end justify-end w-full">
                    <Button
                        variant="outline"
                        size="md:xl sm"
                        onClick={() => handleBulkAction('draft')}
                        disabled={isUploading || quotaLoading}
                    >
                        {isUploading ? (
                            <>
                                <FaSync className="animate-spin mr-2" size={14} />
                                Processing...
                            </>
                        ) : (
                            'Save to Drafts'
                        )}
                    </Button>

                    <div className='flex flex-col gap-2'>

                        {/* Upload Summary */}
                        {uploadedFiles.length > 0 && !quotaLoading && !isUploadLimitReached && (
                            <div className="mt-2 text-center text-xs text-gray-600">
                                {(() => {
                                    const scheduledCount = uploadedFiles.filter(f => f.uploadData.schedule && new Date(f.uploadData.schedule) > new Date()).length;
                                    const immediateCount = uploadedFiles.filter(f => (!f.uploadData.schedule || new Date(f.uploadData.schedule) <= new Date())).length;
                                    const totalPosts = uploadedFiles.length * globalPlatforms.length;

                                    if (globalPlatforms.length === 0) {
                                        return 'No platforms selected - will save as drafts';
                                    }

                                    if (scheduledCount > 0 && immediateCount > 0) {
                                        return `${immediateCount * globalPlatforms.length} posts will upload now, ${scheduledCount * globalPlatforms.length} will be scheduled`;
                                    } else if (scheduledCount > 0) {
                                        return `${scheduledCount * globalPlatforms.length} posts will be scheduled`;
                                    } else if (immediateCount > 0) {
                                        return `${immediateCount * globalPlatforms.length} posts will upload now`;
                                    }
                                    return '';
                                })()}
                            </div>
                        )}
                        <Button
                            variant="default"
                            size="xl"
                            className={`flex flex-col gap-0 items-center ${(isUploading || quotaLoading || isUploadLimitReached || errorMessages.length > 0) ? 'cursor-not-allowed bg-gray-300' : ''}`}
                            onClick={() => {
                                if (!isUploading && !quotaLoading && !isUploadLimitReached && errorMessages.length === 0) {
                                    handleBulkAction('upload');
                                }
                            }}
                            disabled={isUploading || quotaLoading || isUploadLimitReached || errorMessages.length > 0}
                        >
                            {isUploading ? (
                                <div className='flex flex-col gap-0 items-center'>
                                    <FaSync className="animate-spin mb-1" size={14} />
                                    <span className="!text-white text-xs">Processing...</span>
                                </div>
                            ) : (
                                <div className='flex flex-col gap-0 items-center'>
                                    <span className="!text-white">Upload All</span>
                                    <span className="text-[11px] opacity-80 !text-white">(Scheduled & Immediate)</span>
                                </div>
                            )}
                        </Button>
                    </div>
                </div>

                {(quotaLoading || isUploadLimitReached || errorMessages.length > 0) && (
                    <div className="absolute bottom-full left-0 right-0 pb-2 text-center text-xs text-gray-600">
                        {quotaLoading && 'Checking upload limits...'}
                        {isUploadLimitReached && !quotaLoading && 'Upload limit reached - only drafts are available'}
                        {errorMessages.length > 0 && !quotaLoading && !isUploadLimitReached && `${errorMessages.length} error(s) must be fixed before uploading`}
                    </div>
                )}


            </div>


        </div>
    );
};

export default BulkUpload; 